<template>
  <div class="auth-container">
    <div class="auth-card">
      <div class="auth-header">
        <h1 class="auth-title">Create Account</h1>
        <p class="auth-subtitle">Become part of FoodCycle</p>
      </div>

      <form class="auth-form" @submit.prevent="submitRegistration">
        <!-- Account Type Selection -->
        <div class="form-field">
          <label>Account Type</label>
          <div class="account-type-selection">
            <div class="radio-option" :class="{ active: accountType === 'individual' }">
              <input
                type="radio"
                id="individual"
                value="individual"
                v-model="accountType"
              />
              <label for="individual">
                <i class="bi bi-person-circle"></i>
                <span>Private Person</span>
              </label>
            </div>
            <div class="radio-option" :class="{ active: accountType === 'company' }">
              <input
                type="radio"
                id="company"
                value="company"
                v-model="accountType"
              />
              <label for="company">
                <i class="bi bi-building"></i>
                <span>Company</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Company Name Field (only shown for company accounts) -->
        <transition name="slide-fade">
          <div v-if="accountType === 'company'" class="form-field">
            <label for="companyName">Company Name</label>
            <div class="input-wrapper">
              <i class="bi bi-building input-icon"></i>
              <input
                id="companyName"
                type="text"
                v-model="company"
                @blur="validateField('company')"
                :class="{ 'error': touchedFields.company && fieldErrors.company }"
                placeholder="Enter your company name"
                required
              />
            </div>
            <span v-if="touchedFields.company && fieldErrors.company" class="field-error">
              {{ fieldErrors.company }}
            </span>
          </div>
        </transition>

        <div class="form-field">
          <label for="firstName">First Name</label>
          <div class="input-wrapper">
            <i class="bi bi-person input-icon"></i>
            <input
              id="firstName"
              type="text"
              v-model="firstName"
              @blur="validateField('firstName')"
              :class="{ 'error': touchedFields.firstName && fieldErrors.firstName }"
              placeholder="Enter your first name"
              required
            />
          </div>
          <span v-if="touchedFields.firstName && fieldErrors.firstName" class="field-error">
            {{ fieldErrors.firstName }}
          </span>
        </div>

        <div class="form-field">
          <label for="lastName">Last Name</label>
          <div class="input-wrapper">
            <i class="bi bi-person input-icon"></i>
            <input
              id="lastName"
              type="text"
              v-model="lastName"
              @blur="validateField('lastName')"
              :class="{ 'error': touchedFields.lastName && fieldErrors.lastName }"
              placeholder="Enter your last name"
              required
            />
          </div>
          <span v-if="touchedFields.lastName && fieldErrors.lastName" class="field-error">
            {{ fieldErrors.lastName }}
          </span>
        </div>

        <div class="form-field">
          <label for="email">Email</label>
          <div class="input-wrapper">
            <i class="bi bi-envelope input-icon"></i>
            <input
              id="email"
              type="email"
              v-model="email"
              @blur="validateField('email')"
              @input="validateField('email')"
              :class="{ 'error': touchedFields.email && fieldErrors.email }"
              placeholder="Enter your email"
              required
            />
          </div>
          <span v-if="touchedFields.email && fieldErrors.email" class="field-error">
            {{ fieldErrors.email }}
          </span>
        </div>

        <div class="form-field">
          <label for="streetAddress">Street Address</label>
          <div class="input-wrapper">
            <i class="bi bi-geo-alt input-icon"></i>
            <input
              id="streetAddress"
              type="text"
              v-model="streetAddress"
              placeholder="Enter street address"
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-field">
            <label for="city">City</label>
            <div class="input-wrapper">
              <i class="bi bi-building input-icon"></i>
              <input
                id="city"
                type="text"
                v-model="city"
                placeholder="Enter city"
                required
              />
            </div>
          </div>
          
          <div class="form-field">
            <label for="postalCode">Postal Code</label>
            <div class="input-wrapper">
              <i class="bi bi-mailbox input-icon"></i>
              <input
                id="postalCode"
                type="text"
                v-model="postalCode"
                @blur="validateField('postalCode')"
                :class="{ 'error': touchedFields.postalCode && fieldErrors.postalCode }"
                placeholder="Enter postal code"
                required
              />
            </div>
            <span v-if="touchedFields.postalCode && fieldErrors.postalCode" class="field-error">
              {{ fieldErrors.postalCode }}
            </span>
          </div>
        </div>

        <div class="form-field">
          <label for="country">Country</label>
          <div class="input-wrapper">
            <i class="bi bi-globe input-icon"></i>
            <input
              id="country"
              type="text"
              v-model="country"
              placeholder="Enter country"
              required
            />
          </div>
        </div>


        <!-- Membership Section -->
        <div class="membership-section">
          <div class="checkbox-with-info">
            <div class="custom-checkbox">
              <input
                type="checkbox"
                id="becomeMember"
                v-model="becomeMember"
                required
              />
              <label for="becomeMember" class="checkbox-label">
                <span class="checkmark"></span>
                <span class="checkbox-text">I want to become a Food Hub member</span>
              </label>
            </div>
            <button
              type="button"
              class="info-button"
              @click="toggleMembershipInfo"
              :class="{ active: showMembershipInfo }"
            >
              <i class="bi bi-info-circle"></i>
            </button>
          </div>
          
          <transition name="expand">
            <div v-if="showMembershipInfo" class="info-panel">
              <div class="info-content">
                <p>
                  As a Food Hub member you accept our CEFoodCycle commitment to sustainable food practices,
                  reducing food waste, and supporting local communities.
                </p>
                <p>
                  <strong>Key commitments include:</strong>
                </p>
                <ul class="benefits-list">
                  <li>Minimize food waste in your daily activities</li>
                  <li>Support local and sustainable food sources</li>
                  <li>Raise awareness about food waste</li>
                </ul>
                <a :href="pledgeDocumentUrl" target="_blank" rel="noopener noreferrer" class="pledge-link">
                  <i class="bi bi-file-pdf"></i>
                  Read full pledge document
                </a>
              </div>
            </div>
          </transition>
        </div>

        <!-- Error/Success Messages -->
        <transition name="fade">
          <div v-if="errorMessage" class="alert alert-error">
            <i class="bi bi-exclamation-triangle"></i>
            <span>{{ errorMessage }}</span>
          </div>
        </transition>

        <transition name="fade">
          <div v-if="successMessage" class="alert alert-success">
            <i class="bi bi-check-circle"></i>
            <span>{{ successMessage }}</span>
          </div>
        </transition>

        <button type="submit" class="auth-btn" :disabled="!formValid || isLoading">
          <i v-if="isLoading" class="bi bi-arrow-repeat spinner"></i>
          <span v-else>Register</span>
          <i v-if="!isLoading" class="bi bi-arrow-right"></i>
        </button>
        
        <transition name="fade">
          <div v-if="!formValid && !isLoading" class="form-hint">
            <i class="bi bi-info-circle"></i>
            {{ getFormHint }}
          </div>
        </transition>

        <div class="auth-footer">
          <p>
            Already have an account?
            <router-link to="/login" class="auth-link">Log In</router-link>
          </p>
        </div>
      </form>
    </div>
  </div>

  <!-- Registration Success Modal -->
  <Teleport to="body">
    <RegistrationSuccessModal
      v-if="showSuccessModal"
      :email="email"
      @close="showSuccessModal = false"
    />
  </Teleport>
</template>

<script>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import RegistrationSuccessModal from './RegistrationSuccessModal.vue';

export default {
  name: 'RegisterMask',
  components: {
    RegistrationSuccessModal
  },
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();
    const firstName = ref('');
    const lastName = ref('');
    const email = ref('');
    const streetAddress = ref('');
    const city = ref('');
    const postalCode = ref('');
    const country = ref('');
    const company = ref('');
    const accountType = ref('individual');
    const becomeMember = ref(false);
    const showMembershipInfo = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const successMessage = ref('');
    const showSuccessModal = ref(false);
    const fieldErrors = ref({});
    const touchedFields = ref({});

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const postalCodeRegex = /^[A-Za-z0-9\s-]{3,10}$/;

    const validateEmail = () => {
      if (!email.value) {
        fieldErrors.value.email = 'Email is required';
      } else if (!emailRegex.test(email.value)) {
        fieldErrors.value.email = 'Please enter a valid email address';
      } else {
        delete fieldErrors.value.email;
      }
    };

    const validatePostalCode = () => {
      if (!postalCode.value) {
        fieldErrors.value.postalCode = 'Postal code is required';
      } else if (!postalCodeRegex.test(postalCode.value)) {
        fieldErrors.value.postalCode = 'Please enter a valid postal code';
      } else {
        delete fieldErrors.value.postalCode;
      }
    };

    const validateField = (fieldName) => {
      touchedFields.value[fieldName] = true;
      
      switch (fieldName) {
        case 'email':
          validateEmail();
          break;
        case 'postalCode':
          validatePostalCode();
          break;
        case 'firstName':
          if (!firstName.value) {
            fieldErrors.value.firstName = 'First name is required';
          } else {
            delete fieldErrors.value.firstName;
          }
          break;
        case 'lastName':
          if (!lastName.value) {
            fieldErrors.value.lastName = 'Last name is required';
          } else {
            delete fieldErrors.value.lastName;
          }
          break;
        case 'company':
          if (accountType.value === 'company' && !company.value) {
            fieldErrors.value.company = 'Company name is required';
          } else {
            delete fieldErrors.value.company;
          }
          break;
      }
    };

    const formValid = computed(() => {
      const basicFields = firstName.value && lastName.value && email.value && 
                         streetAddress.value && city.value && postalCode.value && country.value;
      const companyField = accountType.value === 'company' ? company.value : true;
      const checkboxes = becomeMember.value;
      const noErrors = Object.keys(fieldErrors.value).length === 0;
      
      return basicFields && companyField && checkboxes && noErrors;
    });

    const pledgeDocumentUrl = computed(() => {
      const publicPath = process.env.BASE_URL || '/dev/';
      const filename = accountType.value === 'company' 
        ? 'pledgebook_business.pdf' 
        : 'pledgebook_citizen.pdf';
      return `${publicPath}${filename}`;
    });

    const getFormHint = computed(() => {
      let missingCount = 0;
      
      if (!firstName.value) missingCount++;
      if (!lastName.value) missingCount++;
      if (!email.value) missingCount++;
      if (!streetAddress.value) missingCount++;
      if (!city.value) missingCount++;
      if (!postalCode.value) missingCount++;
      if (!country.value) missingCount++;
      if (accountType.value === 'company' && !company.value) missingCount++;
      
      const checkboxMissing = !becomeMember.value;
      const hasErrors = Object.keys(fieldErrors.value).length > 0;
      
      if (hasErrors) {
        return 'Fix validation errors';
      }
      
      if (missingCount > 0 && checkboxMissing) {
        return `Fill all fields & accept membership`;
      } else if (missingCount > 0) {
        return `${missingCount} field${missingCount > 1 ? 's' : ''} missing`;
      } else if (checkboxMissing) {
        return 'Accept membership required';
      }
      
      return '';
    });


    const toggleMembershipInfo = () => {
      showMembershipInfo.value = !showMembershipInfo.value;
    };

    const submitRegistration = async () => {
      if (!formValid.value) return;
      
      isLoading.value = true;
      errorMessage.value = '';
      successMessage.value = '';

      const formData = {
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        streetAddress: streetAddress.value,
        city: city.value,
        postalCode: postalCode.value,
        country: country.value,
        accountType: accountType.value,
        company: accountType.value === 'company' ? company.value : null,
        acceptPledge: true,
        becomeMember: becomeMember.value,
      };

      try {
        const resp = await authStore.register(formData);
        if (resp.password) {
          // For development/testing when email is not configured
          successMessage.value = `Your account is created. Password: ${resp.password}`;
          setTimeout(() => {
            router.push('/login');
          }, 3000);
        } else if (resp.message) {
          // Normal flow with email
          showSuccessModal.value = true;
        }
        if (resp.error) {
          errorMessage.value = resp.error;
          isLoading.value = false;
          return;
        }
      } catch (error) {
        console.error('Registration error:', error);
        errorMessage.value = error?.response?.data?.message || 'Registration failed. Please try again.';
      } finally {
        isLoading.value = false;
      }
    };

    return {
      firstName,
      lastName,
      email,
      streetAddress,
      city,
      postalCode,
      country,
      company,
      accountType,
      becomeMember,
      showMembershipInfo,
      formValid,
      pledgeDocumentUrl,
      getFormHint,
      toggleMembershipInfo,
      submitRegistration,
      isLoading,
      errorMessage,
      successMessage,
      showSuccessModal,
      fieldErrors,
      touchedFields,
      validateField,
    };
  },
};
</script>

<style scoped>
.auth-container {
  width: 100%;
  max-width: 480px;
  padding: 2rem 1.5rem;
}

.auth-card {
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 8px 16px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(135, 177, 64, 0.1);
  padding: 2.5rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.auth-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #87B140, #a8d054);
}

.auth-header {
  text-align: center;
  margin-bottom: 2rem;
}

.auth-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.auth-subtitle {
  font-size: 1rem;
  color: #718096;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-field {
  margin-bottom: 0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #4a5568;
  font-size: 0.95rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #87b140;
  font-size: 1.1rem;
  pointer-events: none;
  z-index: 1;
}

input[type="text"], input[type="email"] {
  width: 100%;
  padding: 14px 14px 14px 44px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: #fff;
}

input[type="text"]:focus, input[type="email"]:focus {
  outline: none;
  border-color: #87b140;
  box-shadow: 0 0 0 3px rgba(135, 177, 64, 0.15);
  transform: translateY(-1px);
}

/* Account Type Selection */
.account-type-selection {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 8px;
}

.radio-option {
  position: relative;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fff;
}

.radio-option:hover {
  border-color: #87b140;
  background-color: #f7faec;
}

.radio-option.active {
  border-color: #87b140;
  background-color: #f7faec;
  box-shadow: 0 0 0 2px rgba(135, 177, 64, 0.15);
}

.radio-option input[type="radio"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.radio-option label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  cursor: pointer;
  font-weight: 500;
  color: #4a5568;
}

.radio-option i {
  font-size: 1.5rem;
  color: #87b140;
}

/* Slide fade transition for company field */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Custom Checkbox Styling */
.pledge-section, .membership-section {
  background-color: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.checkbox-with-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  flex: 1;
}

.custom-checkbox input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 0;
  font-weight: 500;
  color: #4a5568;
}

.checkmark {
  width: 22px;
  height: 22px;
  border: 2px solid #cbd5e0;
  border-radius: 6px;
  position: relative;
  transition: all 0.3s ease;
  background-color: white;
  flex-shrink: 0;
}

.custom-checkbox input[type="checkbox"]:checked + .checkbox-label .checkmark {
  background-color: #87b140;
  border-color: #87b140;
}

.custom-checkbox input[type="checkbox"]:checked + .checkbox-label .checkmark::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.info-button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background-color: #e2e8f0;
  color: #718096;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.info-button:hover, .info-button.active {
  background-color: #87b140;
  color: white;
  transform: scale(1.1);
}

/* Info Panel */
.expand-enter-active, .expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from, .expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.expand-enter-to, .expand-leave-from {
  max-height: 400px;
  opacity: 1;
}

.info-panel {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.info-content h4 {
  margin: 0 0 12px 0;
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 600;
}

.info-content p {
  margin: 0 0 12px 0;
  color: #4a5568;
  line-height: 1.5;
}

.info-content ul {
  margin: 0 0 16px 0;
  padding-left: 20px;
  color: #4a5568;
}

.info-content li {
  margin-bottom: 6px;
  line-height: 1.4;
}

.pledge-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #87b140;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.pledge-link:hover {
  color: #75a236;
}

.benefits-grid {
  display: grid;
  gap: 12px;
  margin-bottom: 16px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #4a5568;
  font-size: 0.95rem;
}

.benefit-item i {
  color: #87b140;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.membership-note {
  background-color: #e6f7d3;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #87b140;
  font-size: 0.9rem;
  color: #2d3748;
  margin: 16px 0 0 0;
}

.form-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f7faec;
  border: 1px solid #87b140;
  border-radius: 6px;
  color: #4a5568;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-hint i {
  color: #87b140;
  font-size: 1rem;
}

.auth-btn {
  background: linear-gradient(135deg, #87B140, #a8d054);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: auto;
  width: 100%;
  box-shadow: 0 4px 12px rgba(135, 177, 64, 0.3);
}

.auth-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #75a236, #96c248);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(135, 177, 64, 0.4);
}

.auth-btn:disabled {
  background: linear-gradient(135deg, #cbd5e0, #e2e8f0);
  color: #a0aec0;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  position: relative;
}

.auth-btn:disabled:hover {
  background: linear-gradient(135deg, #cbd5e0, #e2e8f0);
}

.auth-footer {
  text-align: center;
  color: #718096;
  font-size: 0.95rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.auth-link {
  color: #87B140;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.3s ease;
}

.auth-link:hover {
  color: #75a236;
  text-decoration: underline;
}

/* Alert Messages */
.alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  animation: slideDown 0.3s ease;
}

.alert-error {
  background-color: #fee;
  color: #c53030;
  border: 1px solid #fc8181;
}

.alert-success {
  background-color: #f0fdf4;
  color: #2d7e3e;
  border: 1px solid #86efac;
}

.alert i {
  font-size: 1.1rem;
}

/* Spinner Animation */
.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Field Validation */
.field-error {
  display: block;
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 4px;
  animation: fadeIn 0.2s ease;
}

input.error {
  border-color: #dc3545;
}

input.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
}

@media (max-width: 576px) {
  .auth-container {
    padding: 1rem;
  }

  .auth-card {
    padding: 1.5rem;
  }

  .auth-title {
    font-size: 1.5rem;
  }

  .account-type-selection {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .radio-option {
    padding: 12px;
  }

  .radio-option label {
    flex-direction: row;
    justify-content: flex-start;
  }

  input[type="text"], input[type="email"] {
    padding: 12px 12px 12px 40px;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
  }
}
</style>
